import moment from "moment-timezone";
import "moment/locale/es";
import weChamberLogo from "@/assets/images/weChamber/logo-WC-main-nav-drawer.svg";

moment.locale("es");

const DEFAULT_ORG_PICTURE = weChamberLogo;

const unwrap = (post) => (post && post.publication ? post.publication : post) || {};

export const POST_TYPE_OPTIONS = [
  { value: "BLOG", label: "Comunicado", icon: "mdi-text-box-outline" },
  { value: "VIDEO", label: "Video", icon: "mdi-play-circle-outline" },
  { value: "DOC", label: "Documento", icon: "mdi-file-document-outline" },
  { value: "LINK", label: "Enlace", icon: "mdi-link-variant" },
  { value: "POLL", label: "Encuesta", icon: "mdi-poll" },
];

export const POST_TYPE_ICON = POST_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.icon;
  return acc;
}, {});

export const POST_TYPE_LABEL = POST_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

export const resolvePostTypeLabel = (value) => POST_TYPE_LABEL[value] || value || "Comunicado";
export const resolvePostTypeIcon = (value) => POST_TYPE_ICON[value] || "mdi-message-text-outline";

export const getPostId = (post) => {
  const p = unwrap(post);
  return p._id || (post && post._id) || null;
};

export const getPostTitle = (post) => {
  const p = unwrap(post);
  return p.name || "Comunicado sin título";
};

export const getPostDescription = (post) => {
  const p = unwrap(post);
  return p.description || "";
};

export const getPostContent = (post) => {
  const p = unwrap(post);
  return p.value || "";
};

export const getPostType = (post) => {
  const p = unwrap(post);
  return p.type || "BLOG";
};

export const getPostImages = (post) => {
  const p = unwrap(post);
  if (!p) return [];
  if (Array.isArray(p.images)) return p.images.filter(Boolean);
  return [];
};

export const getPostLabels = (post) => {
  const p = unwrap(post);
  if (Array.isArray(p.labels)) return p.labels.filter(Boolean);
  if (typeof p.labels === "string" && p.labels) return [p.labels];
  return [];
};

export const getPostSectionId = (post) => {
  const p = unwrap(post);
  const section = p && p.section;
  if (!section) return null;
  if (typeof section === "object") return section._id || section.id || null;
  return section;
};

export const isPostPinned = (post) => {
  const p = unwrap(post);
  return Boolean(p.pinned);
};

export const getPostDate = (post) => {
  const p = unwrap(post);
  return p.publishedAt || p.createdAt || p.updatedAt || null;
};

export const resolveOrganization = (user) => {
  if (!user) {
    return {
      id: null,
      name: "Cámara WeChamber",
      picture: DEFAULT_ORG_PICTURE,
      initials: "WC",
    };
  }
  const headOffice = user.headOffice;
  if (headOffice && typeof headOffice === "object") {
    const name = headOffice.company || headOffice.name || null;
    const picture = headOffice.profilePicture
      || headOffice.picture
      || headOffice.logo
      || headOffice.companyImage
      || user.profilePicture
      || DEFAULT_ORG_PICTURE;
    const id = headOffice._id || headOffice.id || null;
    return {
      id,
      name: name || "Cámara WeChamber",
      picture,
      initials: getInitialsFromName(name || "Cámara WeChamber"),
    };
  }
  return {
    id: typeof headOffice === "string" ? headOffice : null,
    name: "Cámara WeChamber",
    picture: user.profilePicture || DEFAULT_ORG_PICTURE,
    initials: "CW",
  };
};

const getInitialsFromName = (name) => {
  if (!name || typeof name !== "string") return "WC";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "WC";
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

export const getPostAuthor = (post, organization) => {
  if (organization) return organization;
  return {
    id: null,
    name: "Cámara WeChamber",
    picture: null,
    initials: "CW",
  };
};

export const formatPostDate = (post) => {
  const date = getPostDate(post);
  if (!date) return "";
  return moment(date).format("DD [de] MMMM [de] YYYY · HH:mm");
};

export const formatPostRelative = (post) => {
  const date = getPostDate(post);
  if (!date) return "";
  const now = moment();
  const target = moment(date);
  const diffMinutes = now.diff(target, "minutes");
  if (diffMinutes < 1) return "ahora";
  if (diffMinutes < 60) return `hace ${diffMinutes} min`;
  const diffHours = now.diff(target, "hours");
  if (diffHours < 24) return `hace ${diffHours} h`;
  const diffDays = now.diff(target, "days");
  if (diffDays < 7) return `hace ${diffDays} d`;
  return target.format("DD MMM YYYY");
};

export const extractYouTubeId = (url) => {
  if (!url || typeof url !== "string") return null;
  let id = "";
  if (url.startsWith("https://youtu.be/")) {
    id = url.split("https://youtu.be/")[1] || "";
  } else if (url.includes("?v=")) {
    id = url.split("?v=")[1] || "";
  } else if (url.includes("&v=")) {
    id = url.split("&v=")[1] || "";
  } else if (url.includes("youtube.com/embed/")) {
    id = url.split("youtube.com/embed/")[1] || "";
  }
  id = id.split("&")[0].split("?")[0];
  return id || null;
};

export const getYouTubeEmbedUrl = (post) => {
  const id = extractYouTubeId(getPostContent(post));
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

export const isDocument = (post) => getPostType(post) === "DOC";

export const getDocumentUrl = (post) => {
  const p = unwrap(post);
  if (!p) return null;
  if (p.url) return p.url;
  if (p.value && typeof p.value === "string") return p.value;
  return null;
};

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"];
const PDF_EXTENSIONS = ["pdf"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "mov"];
const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg", "m4a"];

const getExtension = (url) => {
  if (!url || typeof url !== "string") return "";
  try {
    const cleanUrl = url.split("?")[0].split("#")[0];
    const lastSlash = cleanUrl.lastIndexOf("/");
    const lastSegment = lastSlash >= 0 ? cleanUrl.substring(lastSlash + 1) : cleanUrl;
    const dot = lastSegment.lastIndexOf(".");
    if (dot < 0) return "";
    return lastSegment.substring(dot + 1).toLowerCase();
  } catch (e) {
    return "";
  }
};

export const resolveDocumentKind = (post) => {
  const url = getDocumentUrl(post);
  if (!url) return "other";
  const ext = getExtension(url);
  if (!ext) return "other";
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (PDF_EXTENSIONS.includes(ext)) return "pdf";
  if (VIDEO_EXTENSIONS.includes(ext)) return "video";
  if (AUDIO_EXTENSIONS.includes(ext)) return "audio";
  return "other";
};

export const getDocumentName = (post) => {
  const url = getDocumentUrl(post);
  if (!url) return "";
  try {
    const cleanUrl = url.split("?")[0].split("#")[0];
    const lastSlash = cleanUrl.lastIndexOf("/");
    const lastSegment = lastSlash >= 0 ? cleanUrl.substring(lastSlash + 1) : cleanUrl;
    return decodeURIComponent(lastSegment) || "Documento adjunto";
  } catch (e) {
    return "Documento adjunto";
  }
};

export const isLink = (post) => getPostType(post) === "LINK";

export const getLinkUrl = (post) => {
  const p = unwrap(post);
  if (!p) return null;
  if (typeof p.value === "string") return p.value;
  return null;
};

export const sortPostsForFeed = (posts) => {
  const list = [...(posts || [])];
  list.sort((a, b) => {
    const aPin = isPostPinned(a);
    const bPin = isPostPinned(b);
    if (aPin && !bPin) return -1;
    if (!aPin && bPin) return 1;
    const aDate = getPostDate(a);
    const bDate = getPostDate(b);
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return moment(bDate).valueOf() - moment(aDate).valueOf();
  });
  return list;
};
